import { CrystalProvider } from '@applyboard/crystal-ui'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { BrowserRouter, Route, Routes } from 'react-router-dom'
import { ProtectedRoute } from './components/ProtectedRoute'
import {
  ApplicationsListPage,
  CreateApplicationPage,
  LoginPage,
  NotFoundPage,
  ViewApplicationPage,
} from './pages'
import initializers from './config/initializers'
import { HelmetProvider, Helmet } from 'react-helmet-async'
import { SignUpConfirmationPage } from './pages/SignUpConfirmationPage'
import { ConfigProvider } from './utils/ConfigProvider'
import { ErrorElement } from './pages/ErrorPage'
import { ErrorBoundaryWrapper } from './components/ErrorBoundary'
import { MonitorProvider, useGetSpaConfig } from './hooks'
import { ComponentProps, ReactNode } from 'react'
import { LoginCallbackRedirect } from './components/LoginCallbackRedirect/LoginCallbackRedirect'

initializers.init()
const queryClient = new QueryClient()

function CrystalWrapper({ children }: { children: ReactNode }) {
  const { ui, isLoading, school } = useGetSpaConfig()
  const themeColors = ui?.crystalTheme?.colors
  const themeProps: Partial<ComponentProps<typeof CrystalProvider>> = themeColors
    ? { theme: { colors: themeColors } }
    : {}

  if (isLoading) return null

  return (
    <CrystalProvider {...themeProps}>
      <Helmet>
        {ui?.favicon && <link rel="icon" href={ui.favicon} />}
        <title>{school?.name ? `Apply | ${school.name}` : 'AMS'}</title>
      </Helmet>
      {children}
    </CrystalProvider>
  )
}

function App() {
  return (
    <HelmetProvider>
      <Helmet>
        <script src="https://payment.flywire.com/assets/js/checkout.js"></script>
      </Helmet>
      <QueryClientProvider client={queryClient}>
        <CrystalWrapper>
          <ConfigProvider>
            <MonitorProvider>
              <ErrorBoundaryWrapper>
                <BrowserRouter>
                  <Routes>
                    <Route
                      element={<ProtectedRoute />}
                      errorElement={<ErrorElement />}
                    >
                      <Route
                        path="/"
                        element={<ApplicationsListPage />}
                        errorElement={<ErrorElement />}
                      />
                      <Route
                        path="/applications/new"
                        element={<CreateApplicationPage />}
                        errorElement={<ErrorElement />}
                      />
                      <Route
                        path="/applications/:id"
                        element={<ViewApplicationPage />}
                        errorElement={<ErrorElement />}
                      />
                    </Route>
                    <Route
                      element={<ProtectedRoute isSignupLoginRoute />}
                      errorElement={<ErrorElement />}
                    >
                      <Route
                        path="/login"
                        element={<LoginPage />}
                        errorElement={<ErrorElement />}
                      />
                      <Route
                        path="/signup/:username/confirm"
                        element={<SignUpConfirmationPage />}
                        errorElement={<ErrorElement />}
                      />
                    </Route>
                    <Route
                      path="/login/callback"
                      element={<LoginCallbackRedirect />}
                      errorElement={<ErrorElement />}
                    />
                    <Route
                      path="*"
                      element={<NotFoundPage />}
                      errorElement={<ErrorElement />}
                    />
                  </Routes>
                </BrowserRouter>
              </ErrorBoundaryWrapper>
            </MonitorProvider>
          </ConfigProvider>
        </CrystalWrapper>
      </QueryClientProvider>
    </HelmetProvider>
  )
}

export default App
